import { translate } from "../i18n/index.js";
/**
 * الوقت — **المصدر الوحيد لتنسيق أي تاريخ في المنتج**.
 *
 * وقاعدة lint تكسر البناء عند تنسيق تاريخ خارج هذا الملف. والسبب ليس
 * الاتساق: في v2 كانت المنطقة الزمنية تُقرأ من جهاز المتصفّح في نحو ١٠٥
 * مواضع، فكان موظف في الرياض يرى موعد عميلٍ في القاهرة بساعةٍ غير ساعته —
 * وأحياناً **بيومٍ غير يومه**، لأن منتصف الليل عبر الحدّ.
 *
 * وهنا المنطقة **وسيطٌ إلزامي**: لا افتراض للجهاز، ولا نسيان.
 */
/** الأرقام غربية في اللغتين — قرار البلوبرنت §11 */
const NUMBERING = "latn";
function formatter(locale, timeZone, options) {
    return new Intl.DateTimeFormat(`${locale}-u-nu-${NUMBERING}`, { ...options, timeZone });
}
/** تاريخ وحده: ٢٢ سبتمبر ٢٠٢٦ */
export function formatDate(value, locale, timeZone) {
    return formatter(locale, timeZone, {
        day: "numeric",
        month: "long",
        year: "numeric",
    }).format(value);
}
/**
 * **تاريخٌ بلا لحظة** — ٢٢ سبتمبر ٢٠٢٦، بلا ساعةٍ وبلا منطقة.
 *
 * فمدى الخطة **حقيقةُ تقويم لا لحظةٌ على خطّ الزمن**: «أكتوبر» أكتوبر في
 * القاهرة وفي لندن. وعمودُه في القاعدة `date` بلا وقت، ويُقرأ منتصفَ ليلٍ
 * عالميّاً — فمن ينسّقه بمنطقةٍ يزيحه: **«١ أكتوبر» تُعرَض «٣٠ سبتمبر ٣:٠٠ ص»**.
 *
 * وقد وقع ذلك بالفعل، **وظهر بالنقر لا باختبار**: الشاشة تقول إن خطة أكتوبر
 * تبدأ في سبتمبر.
 */
export function formatDateOnly(value, locale) {
    return formatter(locale, "UTC", {
        day: "numeric",
        month: "long",
        year: "numeric",
    }).format(value);
}
/**
 * **شهرٌ في التقويم: «أكتوبر ٢٠٢٦»** — لشهر التقرير وشهر الهدف.
 *
 * وكانا يُنسَّقان بالمنسّق ذي المنطقة، **فيُكتَب «١ أكتوبر» وكأنه يومٌ بعينه**، وفي
 * تنصيبٍ غربيّ المنطقة يصير «٣٠ سبتمبر» — شهرَ غيرِه. فالشهرُ يُقرأ بغرينتش
 * كأخيه «اليوم بلا ساعة»، ولا يُذكَر فيه يوم.
 */
export function formatMonth(value, locale) {
    return formatter(locale, "UTC", { month: "long", year: "numeric" }).format(value);
}
/** قيمةُ حقل الشهر «2026-10» من أوّل الشهر — بغرينتش، فلا يزيحه جهاز */
export function toMonthInputValue(value) {
    return value.toISOString().slice(0, 7);
}
/** وتاريخُ أوّل الشهر من قيمة الحقل — **أو لا شيء** */
export function fromMonthInputValue(value) {
    const match = /^(\d{4})-(\d{2})$/.exec(value);
    if (!match)
        return null;
    const month = Number(match[2]);
    if (month < 1 || month > 12)
        return null;
    return new Date(Date.UTC(Number(match[1]), month - 1, 1));
}
/** وقت وحده: ٢:٠٠ م */
export function formatTime(value, locale, timeZone) {
    return formatter(locale, timeZone, { hour: "numeric", minute: "2-digit" }).format(value);
}
/** تاريخ ووقت معاً */
export function formatDateTime(value, locale, timeZone) {
    return `${formatDate(value, locale, timeZone)} · ${formatTime(value, locale, timeZone)}`;
}
/**
 * **اليوم في تلك المنطقة**، لا في منطقة الجهاز.
 *
 * وهذا ما يجعل «متأخّر» و«اليوم» صحيحَين لموظفٍ في الرياض ينظر إلى موعد
 * عميلٍ في القاهرة.
 */
export function dayKey(value, timeZone) {
    return new Intl.DateTimeFormat("en-CA", {
        timeZone,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
    }).format(value);
}
/**
 * **عنوانُ يومٍ في قائمة** — «النهارده»، «أمس»، وما قبلهما بتاريخه.
 *
 * يقرؤه فاصلُ أيام الشات وأقسامُ صندوق الإشعارات **من موضعٍ واحد**، فلا تقول
 * شاشةٌ «اليوم» وأختُها «النهارده» لنفس اليوم. **وبمنطقة القارئ**: فرسالةٌ بعد
 * منتصف الليل عنده من «النهارده»، لا من «أمس» بتوقيت السيرفر.
 */
export function dayHeading(value, now, locale, timeZone) {
    const word = relativeDay(value, now, timeZone);
    return word === null ? formatDate(value, locale, timeZone) : translate(locale, word);
}
/**
 * **كلمةُ اليوم بالنسبة للنهارده** — النهارده · بكرة · أمس، أو لا كلمة.
 *
 * من موضعٍ واحد يقرؤه عنوانُ القائمة وعنوانُ الأجندة، **فلا يقول الطابور «بكرة»
 * والشات «٢٨ أكتوبر» لنفس اليوم**. و«بكرة» كانت غائبةً عن الاثنين: فطابورُ النشر
 * — وكلُّه مستقبل — كان يكتب تاريخَ الغد بدل «بكرة» (موك أب شادي اليومي).
 */
function relativeDay(value, now, timeZone) {
    const day = dayKey(value, timeZone);
    const shifted = (days) => dayKey(new Date(now.getTime() + days * 86_400_000), timeZone);
    if (day === shifted(0))
        return "time.today";
    if (day === shifted(1))
        return "time.tomorrow";
    if (day === shifted(-1))
        return "time.yesterday";
    return null;
}
/**
 * **عنوانُ يومٍ في أجندة** — «النهارده · الثلاثاء، 27 أكتوبر»، «بكرة · …»، وما بعدهما
 * باسم يومه وتاريخه.
 *
 * لقائمةٍ تنظر للأمام (طابورُ النشر): **الكلمةُ وحدها لا تكفي** لمن يخطّط — يريد اسمَ
 * اليوم ورقمَه أيضاً، كما في الموك أب المعتمَد.
 */
export function agendaDayHeading(value, now, locale, timeZone) {
    const date = formatter(locale, timeZone, {
        weekday: "long",
        day: "numeric",
        month: "long",
    }).format(value);
    const word = relativeDay(value, now, timeZone);
    return word === null
        ? date
        : translate(locale, "time.dayWithDate", { word: translate(locale, word), date });
}
/**
 * **مدّةٌ بكلامٍ لا رقمٌ عارٍ** — «45 دقيقة»، «24 ساعة و32 دقيقة»، «3 أيام و4 ساعات».
 *
 * وكان متوسطُ الردّ يُعرَض «1472» بلا وحدة: رقمٌ لا يعرف قارئُه أهو دقائق أم ساعات.
 * فالمدّةُ تُقال بأكبر وحدتين معقولتين، **والأيامُ من يومين فأكثر** — فـ«يوم واحد»
 * لمدّة ٢٤ ساعة و٣٢ دقيقة تُسقط نصفَ ساعةٍ يُسأل عنها.
 */
export function formatDuration(totalMinutes, locale) {
    const minutes = Math.max(0, Math.round(totalMinutes));
    /**
     * **والوحدةُ بالمصري من القاموس** — «ساعتين» و«5 دقايق»، لا «ساعتان» و«5 دقائق»
     * كما يكتبها منسّقُ المتصفّح بالفصحى. وقاعدةُ الجمع (واحد · اتنين · قليل · كتير)
     * من `Intl.PluralRules` نفسِه، فلا تُحسَب بيدٍ تُخطئ.
     */
    const plural = new Intl.PluralRules(locale);
    const unit = (value, name) => {
        const rule = plural.select(value);
        const form = rule === "one" || rule === "two" || rule === "few" ? rule : "other";
        return translate(locale, `time.${name}.${form}`, { n: value });
    };
    const pair = (a, b) => translate(locale, "time.durationPair", { a, b });
    if (minutes < 60)
        return unit(minutes, "minute");
    const hours = Math.floor(minutes / 60);
    if (hours < 48) {
        const rest = minutes % 60;
        return rest === 0 ? unit(hours, "hour") : pair(unit(hours, "hour"), unit(rest, "minute"));
    }
    const days = Math.floor(hours / 24);
    const rest = hours % 24;
    return rest === 0 ? unit(days, "day") : pair(unit(days, "day"), unit(rest, "hour"));
}
/**
 * **المناطقُ المعروفة بأسمائها** — «القاهرة» لا `Africa/Cairo`.
 *
 * فكان «بتوقيت Africa/Cairo» يُكتَب في وجه العميل وفي نافذة العنصر: اسمُ قاعدة
 * بيانات المناطق لا اسمُ مدينة. والقائمةُ نفسُها هي ما يُختار منه في المعالج وفي
 * العميل — **فمنطقةٌ تُختار لها اسمٌ دائماً**؛ وما يأتي من خارجها يُقال باسم مدينته.
 */
const ZONE_NAME = {
    "Africa/Cairo": "time.zone.cairo",
    "Asia/Riyadh": "time.zone.riyadh",
    "Asia/Dubai": "time.zone.dubai",
    "Asia/Kuwait": "time.zone.kuwait",
    "Asia/Qatar": "time.zone.doha",
    "Asia/Amman": "time.zone.amman",
    "Europe/London": "time.zone.london",
    UTC: "time.zone.utc",
};
export const KNOWN_TIME_ZONES = Object.keys(ZONE_NAME);
export function timeZoneLabel(zone, locale) {
    const key = ZONE_NAME[zone];
    if (key !== undefined)
        return translate(locale, key);
    const city = zone.slice(zone.lastIndexOf("/") + 1).replace(/_/g, " ");
    return city === "" ? zone : city;
}
/**
 * حالة الموعد بالنسبة للآن — **حسابٌ واحد** تقرؤه القائمة والبطاقة والعدّاد.
 *
 * في v2 كان كل موضع يحسبها بنفسه، فاختلف عدّاد «متأخر» في الرئيسية عن عدد
 * السطور الحمراء في القائمة — واكتشفه مستخدم، لا اختبار.
 */
export function dueState(due, now, timeZone) {
    if (due.getTime() < now.getTime())
        return "overdue";
    const dueDay = dayKey(due, timeZone);
    const today = dayKey(now, timeZone);
    if (dueDay === today)
        return "today";
    const in24h = new Date(now.getTime() + 86_400_000);
    return due.getTime() <= in24h.getTime() ? "soon" : "later";
}
export function dueGroup(due, now, timeZone) {
    const state = dueState(due, now, timeZone);
    if (state === "overdue" || state === "today")
        return state;
    const days = (Date.parse(dayKey(due, timeZone)) - Date.parse(dayKey(now, timeZone))) / 86_400_000;
    return days <= 6 ? "week" : "later";
}
/** يومُ الأسبوع وحده: «الخميس» */
export function formatWeekday(value, locale, timeZone) {
    return formatter(locale, timeZone, { weekday: "long" }).format(value);
}
/** يومٌ وشهر بلا سنة: «١٩ أكتوبر» — لموعدٍ قريبٍ سنتُه معروفة */
export function formatDayMonth(value, locale, timeZone) {
    return formatter(locale, timeZone, { day: "numeric", month: "long" }).format(value);
}
/** **كتلةُ التاريخ** — رقمُ اليوم واسمُ الشهر منفصلين، كما تُرسَم على صفّ الاجتماع */
export function dateBlock(value, locale, timeZone) {
    return {
        day: formatter(locale, timeZone, { day: "numeric" }).format(value),
        month: formatter(locale, timeZone, { month: "long" }).format(value),
    };
}
/**
 * **الموعدُ في كلمةٍ على صفّ** — بحسب مجموعته.
 *
 * فالمتأخّرُ «من يومين»، وموعدُ النهارده ساعتُه، وما في الأسبوع يومُه، وما بعده
 * تاريخُه. **ومن هنا وحده**، فلا يقول صفٌّ «الخميس» وجارُه «٣٠ أكتوبر» لليوم نفسِه.
 */
export function formatDueShort(due, now, locale, timeZone) {
    const group = dueGroup(due, now, timeZone);
    if (group === "overdue")
        return relativeTime(due, now, locale);
    if (group === "today")
        return formatTime(due, locale, timeZone);
    if (group === "week")
        return formatWeekday(due, locale, timeZone);
    return formatDayMonth(due, locale, timeZone);
}
/**
 * «من ١٨ دقيقة» — بصيغة النظام لا بمكتبة خارجية.
 *
 * وتُقرَّب إلى أكبر وحدة معقولة: «من ساعتين» أوضح من «من ١٣٧ دقيقة».
 */
export function relativeTime(value, now, locale) {
    const seconds = Math.round((value.getTime() - now.getTime()) / 1000);
    const absolute = Math.abs(seconds);
    const format = new Intl.RelativeTimeFormat(`${locale}-u-nu-${NUMBERING}`, { numeric: "auto" });
    const units = [
        ["year", 31_536_000],
        ["month", 2_592_000],
        ["week", 604_800],
        ["day", 86_400],
        ["hour", 3_600],
        ["minute", 60],
    ];
    for (const [unit, size] of units) {
        if (absolute >= size)
            return format.format(Math.round(seconds / size), unit);
    }
    return format.format(Math.round(seconds), "second");
}
/**
 * قيمة حقل التاريخ والوقت في الشاشة، **بالمنطقة المقصودة**.
 *
 * حقل المتصفّح الأصلي يقرأ ويكتب بمنطقة الجهاز دائماً — وهي عائلة أعطال
 * كاملة في v2، منها قيمة تاريخ تُحفظ **بيوم أمس**. فالمكوّن المشترك يستعمل
 * هذه الدالة وأختها بدلاً منه.
 */
export function toLocalInputValue(value, timeZone) {
    const parts = new Intl.DateTimeFormat("en-CA", {
        timeZone,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
    }).formatToParts(value);
    const get = (type) => parts.find((p) => p.type === type)?.value ?? "00";
    // `hour` قد تأتي "24" عند منتصف الليل في بعض البيئات
    const hour = get("hour") === "24" ? "00" : get("hour");
    return `${get("year")}-${get("month")}-${get("day")}T${hour}:${get("minute")}`;
}
/**
 * من قيمة الحقل إلى لحظة حقيقية، **مقروءةً في المنطقة المقصودة**.
 *
 * `new Date("2026-09-22T14:00")` يقرؤها المتصفّح بمنطقته هو. وهنا نحسب الفارق
 * ونطرحه، فتكون الثانية عشرة ظهراً بتوقيت القاهرة هي الثانية عشرة ظهراً
 * بتوقيت القاهرة مهما كان جهاز من يكتبها.
 */
export function fromLocalInputValue(value, timeZone) {
    const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/.exec(value);
    if (!match)
        return null;
    const [, y, mo, d, h, mi] = match;
    const asUtc = Date.UTC(Number(y), Number(mo) - 1, Number(d), Number(h), Number(mi));
    // الفارق بين تلك اللحظة كما تُقرأ في المنطقة وكما هي بتوقيت جرينتش
    const probe = new Date(asUtc);
    const readBack = Date.parse(`${toLocalInputValue(probe, timeZone)}:00Z`);
    return new Date(asUtc + (asUtc - readBack));
}
/**
 * **قيمةُ حقل اليوم — تقويمٌ بلا منطقة.**
 *
 * فمدى الخطة يومٌ في التقويم لا لحظة: عمودُه `date`، ويُقرأ منتصفَ ليلٍ عالميّاً
 * — وهو ما يقوله `formatDateOnly`. **فحقلُه يُكتَب ويُقرأ بغرينتش وحدها**، ولو
 * قُرئ بمنطقة الجهاز لصار أوّلُ أكتوبر ثلاثين سبتمبر عند من يتأخّر عنها.
 */
/**
 * **شهرٌ في التقويم** — يبدأ بالسبت. والتقويمُ يومٌ بلا منطقة: فالحساب بالتوقيت العالمي.
 */
export function calendarMonth(year, month, locale) {
    const tag = locale === "ar" ? "ar-EG-u-nu-latn" : "en-GB";
    const first = new Date(Date.UTC(year, month - 1, 1));
    const count = new Date(Date.UTC(year, month, 0)).getUTCDate();
    const lead = (first.getUTCDay() + 1) % 7;
    const pad = (n) => String(n).padStart(2, "0");
    const weekday = new Intl.DateTimeFormat(tag, { weekday: "narrow", timeZone: "UTC" });
    return {
        label: formatMonth(first, locale),
        /** أوّلُ يناير ٢٠٠٠ سبت — يومٌ معروفٌ يبدأ منه الأسبوع */
        weekdays: Array.from({ length: 7 }, (_, at) => weekday.format(new Date(Date.UTC(2000, 0, 1 + at)))),
        days: [
            ...Array.from({ length: lead }, () => null),
            ...Array.from({ length: count }, (_, at) => `${String(year)}-${pad(month)}-${pad(at + 1)}`),
        ],
    };
}
export function toDayInputValue(value) {
    return value.toISOString().slice(0, 10);
}
/** ومن قيمة الحقل إلى منتصف ليل ذلك اليوم عالميّاً — **أو لا شيء** */
export function fromDayInputValue(value) {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
    if (!match)
        return null;
    const [, y, mo, d] = match;
    const at = new Date(Date.UTC(Number(y), Number(mo) - 1, Number(d)));
    return toDayInputValue(at) === value ? at : null;
}
//# sourceMappingURL=index.js.map